/**
 * SUPPORT: the case a person decides, and the payments the executor moves
 * (docs/plans/support-decides-stripe-executes-2026-10-18.html; issue #2272).
 *
 * A clean order (delivered, nothing removed, no guest report, nothing from
 * Stripe we did not ask for) settles by itself. Every other order gets ONE
 * open case, decided in one table (`projections/support-case-table.ts`): what
 * goes back to the guest per line, and who pays for each line. A bank event
 * (dispute, fraud warning) always opens a case of its own. The decided table
 * is the record; Stripe only carries it out.
 *
 * Every response states every field in every arm: no `.default()` is relied on.
 */
import { z } from 'zod';
import { CurrencyCode, Instant, MinorUnitsNonNegative, PageRequest, Uuid } from '../common';
import { defineEndpoint } from '../http';
import {
  GUEST_PRESETS,
  OPENING_REASON_KINDS,
  OTHER_REASON_MAX,
  PAYER_PRESETS,
  PERSON_REASON_KINDS,
  SUPPORT_REASON_KINDS,
  SUPPORT_ROW_KINDS,
  SUPPORT_ROW_WHY,
  SUPPORT_TABLE_ISSUES,
  SUPPORT_TABLE_MODES,
} from '../projections/support-case-table';
import {
  getOrderDetail,
  PaymentMovementKind,
  PaymentMovementRow,
  PaymentMovementStatus,
} from './orders';

export const SupportReasonKind = z.enum(SUPPORT_REASON_KINDS);
export const PersonReasonKind = z.enum(PERSON_REASON_KINDS);
export const SupportCaseKind = z.enum(['ORDER', 'BANK']);
export const SupportCaseStatus = z.enum(['OPEN', 'DECIDED']);
export const SupportTableMode = z.enum(SUPPORT_TABLE_MODES);
export const GuestPreset = z.enum(GUEST_PRESETS);
export const PayerPreset = z.enum(PAYER_PRESETS);
export const SupportTableIssueCode = z.enum(SUPPORT_TABLE_ISSUES);
/** What Stripe reported about a dispute: null while it has not decided. */
export const BankOutcome = z.enum(['WON', 'LOST']);

/** The text an OTHER reason carries: what the reason is, the case's title. */
export const OtherReasonText = z.string().trim().min(1).max(OTHER_REASON_MAX);
/** The free note on a case. */
export const SupportCaseDetailText = z.string().max(2000);

/** One reason on a case: what opened it or joined it, by whom, when. */
export const SupportCaseReason = z.object({
  /** Its place on the case: what an edit names. */
  index: z.int().min(0),
  kind: SupportReasonKind,
  /** AUTOMATIC reasons are read-only; a PERSON's non-bank reason may be changed while OPEN. */
  source: z.enum(['AUTOMATIC', 'PERSON']),
  /** Who: a person's address, or Counter, Guest, Bank, Stripe, Platform. */
  by: z.string().max(160),
  at: Instant,
  /** The Stripe object it is about (a dispute, a warning, a refund), when one. */
  ref: z.string().max(120).nullable(),
  /** OTHER: which reason. REFUND_REFUSED: Stripe's reason. */
  text: z.string().max(OTHER_REASON_MAX).nullable(),
  /** A Stripe amount the reason states (a refund made in the dashboard, money captured late). */
  amountMinor: MinorUnitsNonNegative.nullable(),
});
export type SupportCaseReason = z.infer<typeof SupportCaseReason>;

/** One line of the order, as the table shows it (`SupportTableRow`). */
export const SupportTableRow = z.object({
  key: z.string().min(1).max(160),
  kind: z.enum(SUPPORT_ROW_KINDS),
  label: z.string().max(200),
  quantity: z.int().min(0).nullable(),
  guestPriceMinor: MinorUnitsNonNegative,
  bakerPriceMinor: MinorUnitsNonNegative,
  alreadyMinor: MinorUnitsNonNegative,
  received: z.boolean(),
  supplied: z.boolean(),
  reported: z.boolean(),
  why: z.enum(SUPPORT_ROW_WHY).nullable(),
});
export type SupportTableRow = z.infer<typeof SupportTableRow>;

/** One row of the table as a person fills it (`SupportDecisionRow`). */
export const SupportDecisionRow = z.object({
  key: z.string().min(1).max(160),
  outMinor: MinorUnitsNonNegative,
  bakeryMinor: MinorUnitsNonNegative,
  courierMinor: MinorUnitsNonNegative,
  croissantMinor: MinorUnitsNonNegative,
});
export type SupportDecisionRow = z.infer<typeof SupportDecisionRow>;

/** The table a person submits. The server recomputes the rows and refuses any issue. */
export const SupportTableSubmission = z.object({
  guestPreset: GuestPreset,
  payerPreset: PayerPreset.nullable(),
  rows: z.array(SupportDecisionRow).max(200),
  note: SupportCaseDetailText,
  /** Only on a case Stripe's refusal reopened: the guest was paid outside Stripe; nothing is sent. */
  paidOutside: z.boolean().optional(),
});
export type SupportTableSubmission = z.infer<typeof SupportTableSubmission>;

/** A decided case's table, as stored: the rows it was decided on, beside the cells. */
export const SupportDecision = z.object({
  mode: SupportTableMode,
  guestPreset: GuestPreset,
  payerPreset: PayerPreset.nullable(),
  courierPriceMinor: MinorUnitsNonNegative,
  rows: z.array(SupportTableRow.extend(SupportDecisionRow.shape)),
  note: SupportCaseDetailText,
  /** The guest was paid outside Stripe: its refund recorded as done, nothing sent. */
  paidOutside: z.boolean(),
});
export type SupportDecision = z.infer<typeof SupportDecision>;

/** The table's context (`SupportTableContext`). */
export const SupportTableContext = z.object({
  mode: SupportTableMode,
  unlocked: z.boolean(),
  fixedOutMinor: MinorUnitsNonNegative,
  courierPriceMinor: MinorUnitsNonNegative,
});

/** A case as a list row and as the head of its page. */
export const SupportCaseRecord = z.object({
  id: Uuid,
  /** Displayed "Case 1000". */
  code: z.int().min(1000),
  orderId: Uuid,
  orderCode: z.string().max(16),
  kind: SupportCaseKind,
  status: SupportCaseStatus,
  reasons: z.array(SupportCaseReason),
  /** The order's lines a person marked reported by the guest (product row keys). */
  reportedLineIds: z.array(z.string().max(160)),
  detail: SupportCaseDetailText,
  houseName: z.string().max(160).nullable(),
  guestName: z.string().max(160).nullable(),
  openedAt: Instant,
  /** 24 hours after opening; a dispute 3 days before Stripe's evidence deadline. */
  dueAt: Instant,
  overdue: z.boolean(),
  unlockedBy: z.string().max(160).nullable(),
  unlockedAt: Instant.nullable(),
  decidedBy: z.string().max(160).nullable(),
  decidedAt: Instant.nullable(),
  /** What the decided table gives back (a dispute: what the bank takes); null while open. */
  decidedOutMinor: MinorUnitsNonNegative.nullable(),
  bankOutcome: BankOutcome.nullable(),
  currency: CurrencyCode,
});
export type SupportCaseRecord = z.infer<typeof SupportCaseRecord>;

export const listSupportCases = defineEndpoint({
  method: 'GET',
  path: '/internal/support/cases',
  mount: 'internal',
  summary: 'The Support queue: open or decided cases, newest first, with the counts (issue #2272)',
  // OPEN when not named: the queue a person reads first, and a bare listing a
  // caller can address the case page from (check-demo-health reads its ids).
  query: PageRequest.extend({ status: SupportCaseStatus.default('OPEN') }),
  response: z.object({
    items: z.array(SupportCaseRecord),
    nextCursor: z.string().nullable(),
    counts: z.object({ open: z.int().min(0), overdue: z.int().min(0) }),
  }),
});

/**
 * The open and overdue counts alone: the rail's badge, asked every 15 seconds
 * on every screen, and the Cases tab's count - neither draws a case.
 */
export const SupportCounts = z.object({ open: z.int().min(0), overdue: z.int().min(0) });

export const getSupportCounts = defineEndpoint({
  method: 'GET',
  path: '/internal/support/counts',
  mount: 'internal',
  summary: 'How many Support cases are open, and how many of them overdue (issue #2272)',
  response: SupportCounts,
});

/** The case page: the case, its table, what happened, and its neighbours. */
export const SupportCaseView = z.object({
  case: SupportCaseRecord,
  rows: z.array(SupportTableRow),
  context: SupportTableContext,
  /** The stored table once decided; null while open. */
  decision: SupportDecision.nullable(),
  /** The viewer may unlock the guest column (an admin). */
  viewerIsAdmin: z.boolean(),
  /** Deciding cancels the order first: a fraud warning on an order not made yet. */
  decisionCancelsOrder: z.boolean(),
  /** The order's timeline, exactly as the order page draws it. */
  timeline: getOrderDetail.response.shape.events,
  /** Every other case on this order. */
  otherCases: z.array(
    z.object({
      id: Uuid,
      code: z.int().min(1000),
      kind: SupportCaseKind,
      status: SupportCaseStatus,
      reasons: z.array(SupportReasonKind),
      decidedOutMinor: MinorUnitsNonNegative.nullable(),
    }),
  ),
  /** The other orders the same payment paid for, each with its open case if it has one. */
  paidTogether: z.array(
    z.object({
      orderId: Uuid,
      orderCode: z.string().max(16),
      serviceDate: z.string().max(10),
      openCaseId: Uuid.nullable(),
      openCaseCode: z.int().min(1000).nullable(),
    }),
  ),
});
export type SupportCaseView = z.infer<typeof SupportCaseView>;

export const getSupportCase = defineEndpoint({
  method: 'GET',
  path: '/internal/support/cases/:caseId',
  mount: 'internal',
  summary: 'One Support case: its table rows, timeline and the other cases (issue #2272)',
  params: z.object({ caseId: Uuid }),
  response: SupportCaseView,
});

export const openSupportCase = defineEndpoint({
  method: 'POST',
  path: '/internal/support/cases',
  mount: 'internal',
  summary:
    'Open a case by hand; a non-bank reason joins the order open case, a bank one opens its own (issue #2272)',
  body: z
    .object({
      /** Exactly one of the order's id and its code (the code on its bag). */
      orderId: Uuid.optional(),
      orderCode: z.string().trim().min(1).max(16).optional(),
      reason: z.enum(OPENING_REASON_KINDS),
      /** Required with OTHER, refused with anything else. */
      otherText: OtherReasonText.optional(),
      detail: SupportCaseDetailText.optional(),
    })
    .refine((body) => (body.orderId === undefined) !== (body.orderCode === undefined), {
      message: 'exactly one of orderId and orderCode',
    }),
  idempotent: true,
  response: z.object({
    caseId: Uuid,
    code: z.int().min(1000),
    /** The reason joined the order's open case rather than opening one. */
    joined: z.boolean(),
  }),
});

export const updateSupportCase = defineEndpoint({
  method: 'PATCH',
  path: '/internal/support/cases/:caseId',
  mount: 'internal',
  summary:
    'Edit an open case: its detail, the lines the guest reported, a person-added reason (issue #2272)',
  params: z.object({ caseId: Uuid }),
  body: z.object({
    detail: SupportCaseDetailText.optional(),
    /** The full set of received product lines marked reported by the guest. */
    reportedLineIds: z.array(z.string().min(1).max(160)).max(200).optional(),
    /** Person-added reasons to change, by index; among the non-bank reasons only. */
    reasons: z
      .array(
        z.object({
          index: z.int().min(0),
          kind: PersonReasonKind,
          otherText: OtherReasonText.optional(),
        }),
      )
      .max(20)
      .optional(),
  }),
  idempotent: false,
  response: SupportCaseView,
});

export const unlockSupportCase = defineEndpoint({
  method: 'POST',
  path: '/internal/support/cases/:caseId/unlock',
  mount: 'internal',
  summary: 'An admin unlocks (or locks again) the guest column of an open case (issue #2272)',
  params: z.object({ caseId: Uuid }),
  body: z.object({ unlocked: z.boolean() }),
  idempotent: true,
  response: SupportCaseView,
});

export const decideSupportCase = defineEndpoint({
  method: 'POST',
  path: '/internal/support/cases/:caseId/decide',
  mount: 'internal',
  summary:
    'Decide an open case with its table; the server recomputes the rows and refuses any issue (issue #2272)',
  params: z.object({ caseId: Uuid }),
  body: SupportTableSubmission,
  idempotent: true,
  response: SupportCaseView,
});

// ---------------------------------------------------------------------------
// Payments: the Stripe executor's instructions (packages/services/src/stripe-executor)
// ---------------------------------------------------------------------------

/* The movement row is the order page's too, so it is stated beside it (orders.ts). */
export { PaymentMovementKind, PaymentMovementRow, PaymentMovementStatus };

/** Something Stripe did that no instruction of ours asked for; it opened a case. */
export const UnrequestedStripeObject = z.object({
  stripeRef: z.string().max(120),
  kind: z.enum(['REFUND', 'CAPTURE']),
  amountMinor: MinorUnitsNonNegative,
  currency: CurrencyCode,
  orderId: Uuid,
  orderCode: z.string().max(16),
  caseId: Uuid.nullable(),
  caseCode: z.int().min(1000).nullable(),
  at: Instant,
});

export const listPaymentMovements = defineEndpoint({
  method: 'GET',
  path: '/internal/support/payments',
  mount: 'internal',
  summary: 'The executor instructions to Stripe and their state, newest first (issue #2272)',
  query: PageRequest.extend({
    status: PaymentMovementStatus.optional(),
    /** One case's instructions only. */
    caseId: Uuid.optional(),
  }),
  response: z.object({
    items: z.array(PaymentMovementRow),
    nextCursor: z.string().nullable(),
    counts: z.object({
      pending: z.int().min(0),
      sent: z.int().min(0),
      confirmed: z.int().min(0),
      failed: z.int().min(0),
      needsHuman: z.int().min(0),
      /** Not confirmed yet and being retried on their own: refunds with an attempt behind them, transfer rows the sweep retries. */
      retrying: z.int().min(0),
      /** Confirmed by Stripe on the platform's day, in Paris. */
      confirmedToday: z.int().min(0),
    }),
    unrequested: z.array(UnrequestedStripeObject),
  }),
});

export const retryPaymentMovement = defineEndpoint({
  method: 'POST',
  path: '/internal/support/payments/:movementId/retry',
  mount: 'internal',
  summary: 'Send one failed or stuck instruction to Stripe again now (issue #2272)',
  params: z.object({ movementId: z.string().min(1).max(80) }),
  body: z.object({}),
  idempotent: true,
  response: z.object({ movement: PaymentMovementRow }),
});

export const supportEndpoints = {
  listSupportCases,
  getSupportCounts,
  getSupportCase,
  openSupportCase,
  updateSupportCase,
  unlockSupportCase,
  decideSupportCase,
  listPaymentMovements,
  retryPaymentMovement,
} as const;
